import * as React from 'react';
import { ProhibitPropsFrom, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { FilteringList, FilteringListMessages, FilteringListProps, ListItem } from './FilteringList';
import { ListItemFilterFn, standardItemFilter } from './ListItemUtils';

export { ListItem, ListItemFilterFn };

export type LocallyFilteringListProps = ProhibitPropsFrom<FilteringListProps, 'onFilterChange' | 'searchText'> & {
  /**
   * The default item comparator includes a case-insensitive comparison on `ListItem.value`. If you want to keep the same
   * filtering behaviors (i.e. cleaning up separators) but use a different comparator, provide it here.
   */
  itemFilterFn?: ListItemFilterFn;
};

export interface LocallyFilteringListState {
  filteredItems: ListItem[];
  searchText: string;
}

/** `FilteringList` with default filtering behavior. */
export class LocallyFilteringList extends React.PureComponent<PropsAndElementAttrs<LocallyFilteringListProps>, LocallyFilteringListState> {
  private listRef: React.RefObject<FilteringList> = React.createRef();

  public state: LocallyFilteringListState = LocallyFilteringList.defaultStateFor(this.props);

  private static defaultStateFor(props: LocallyFilteringListProps): LocallyFilteringListState {
    return {
      filteredItems: props.items,
      searchText: '',
    };
  }

  public componentDidUpdate(prevProps: LocallyFilteringListProps): void {
    if (prevProps.items !== this.props.items || prevProps.itemFilterFn !== this.props.itemFilterFn) {
      this.handleFilterChange(this.state.searchText);
    }
  }

  /** Gets the index into `props.items` of the pseudo-focused listbox option. */
  public getActiveItemIndex(): number {
    return this.listRef.current?.getActiveItemIndex() ?? -1;
  }

  private handleFilterChange = (value: string): void => {
    const filteredItems = standardItemFilter(this.props.items, value, this.props.itemFilterFn);
    if (filteredItems.length === 0) {
      filteredItems.push({ value: FilteringListMessages.noMatches(), disabled: true });
    }
    this.setState({
      searchText: value,
      filteredItems,
    });
  }

  public render(): JSX.Element {
    const { items, itemFilterFn, ...listProps } = this.props;
    return (
      <FilteringList
        {...listProps}
        items={this.state.filteredItems}
        onFilterChange={this.handleFilterChange}
        ref={this.listRef}
        searchText={this.state.searchText}
      />
    );
  }
}
